import React, { useEffect } from 'react';

import { Card } from '@/components/ui/cards';
import { Selector } from '@/components/ui/inputs/selector';
import { PlaceholderContent } from '@/components/ui/layout';
import { useTranslation } from '@/i18n';
import { useMyTeamFilters } from '@/hooks/attendance/myTeam/useMyTeamFilters';


export interface MyTeamTabContentProps {
  // Reportan el estado de carga y la función de refetch al dashboard padre: el botón
  // global "Actualizar" necesita saber si la pestaña activa ya cargó y disparar su
  // propio refetch, aunque esta pestaña administre su propio hook de datos.
  onLoadingChange?: (isLoading: boolean) => void;
  onRefetchReady?: (refetch: () => void) => void;
}

// "Mi equipo" tab content (ASIS-5402 / REXASIS-001 — first of a 13-ticket epic).
// Owns the 3-filter panel (Secciones / Empresas / Colaboradores) and renders one
// placeholder container per section returned by the backend — the real content of
// "Situación de asistencia" / "Resumen del mes" ships in later tickets of the epic.
export const MyTeamTabContent: React.FC<MyTeamTabContentProps> = ({
  onLoadingChange,
  onRefetchReady,
}) => {
  const { t: translate } = useTranslation();
  const {
    isLoading,
    filters,
    sections,
    catalogOptions,
    visibleSectionCodes,
    isEmployeesLoading,
    handlers,
    refetch,
  } = useMyTeamFilters();

  useEffect(() => { onLoadingChange?.(isLoading); }, [isLoading, onLoadingChange]);
  useEffect(() => { onRefetchReady?.(refetch); }, [refetch, onRefetchReady]);

  const allLabel = translate('attendance:dashboard.myTeam.filters.all');

  return (
    <div className="flex flex-col gap-6" data-testid="my-team-tab-content">
      <Card elevation={2} className="rounded-2xl" data-testid="my-team-filters-panel">
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
          <Selector
            label={translate('attendance:dashboard.myTeam.filters.section')}
            value={filters.sectionCodes}
            options={catalogOptions.sectionOptions}
            onChange={handlers.onSectionChange}
            multiple
            searchable
            isLoading={isLoading}
            placeholder={allLabel}
            data-testid="my-team-section-selector"
          />
          <Selector
            label={translate('attendance:dashboard.myTeam.filters.company')}
            value={filters.companyIds}
            options={catalogOptions.companyOptions}
            onChange={handlers.onCompanyChange}
            multiple
            searchable
            isLoading={isLoading}
            placeholder={allLabel}
            data-testid="my-team-company-selector"
          />
          <Selector
            label={translate('attendance:dashboard.myTeam.filters.employee')}
            value={filters.employeeIds}
            options={catalogOptions.employeeOptions}
            onChange={handlers.onEmployeeChange}
            multiple
            searchable
            isLoading={isLoading || isEmployeesLoading}
            placeholder={allLabel}
            data-testid="my-team-employee-selector"
          />
        </div>
      </Card>

      <div className="flex flex-col gap-6">
        {sections
          .filter((section) => visibleSectionCodes.includes(section.code))
          .map((section) => (
            <Card
              key={section.code}
              elevation={1}
              className="rounded-2xl"
              data-testid={`my-team-section-${section.code}`}
            >
              <PlaceholderContent label={section.label} />
            </Card>
          ))}
      </div>
    </div>
  );
};

export default MyTeamTabContent;
